import { Select as AntSelect, Table } from "antd";
import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import Input, { InputType } from "components/shared/inputs/input";
import React, { useEffect, useMemo, useState } from "react";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import {
  useCreateAccessoryMutation,
  useGetEnvironnementsQuery,
  useLazyGetAccessoryDepIncQuery,
  useLazyGetAccessoryGroupsQuery,
  useLazyGetAllAccessoriesQuery,
  useUpdateAccessoryMutation,
} from "store/api";
import type { DatasourceMutationRequest } from "./TvaPopup";

type TableRecord = Record<string, any>;

type EnvironmentOption = {
  value: string;
  code: string;
  label: string;
};

type AccessoryOption = {
  id: string;
  code: string;
  reference: string;
  label: string;
};

type RelationRow = {
  id: string;
  code: string;
  reference: string;
};

type GroupOption = {
  value: string;
  label: string;
  text: string;
};

type AccessorySuperEditPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  mode?: "create" | "edit";
  row?: TableRecord | null;
  envNumeric?: number;
  lang: string;
  onSaved?: () => Promise<void> | void;
};

function toCheckedEnvMap(row: TableRecord | null | undefined, environments: EnvironmentOption[]) {
  const selected = row?.envs;
  let values: string[] = [];

  if (Array.isArray(selected)) {
    values = selected
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "object") {
          const env = item as Record<string, unknown>;
          const raw = env.value ?? env.id ?? env.code;
          return typeof raw === "string" || typeof raw === "number" ? String(raw) : "";
        }
        return String(item);
      })
      .map((item) => item.trim())
      .filter(Boolean);
  } else if (typeof selected === "string") {
    values = selected.split(",").map((item) => item.trim()).filter(Boolean);
  }

  return environments.reduce<Record<string, boolean>>((acc, env) => {
    acc[env.code] = values.includes(env.value) || values.includes(env.code);
    return acc;
  }, {});
}

function getCheckedEnvIds(stateEnvs: Record<string, boolean>, environments: EnvironmentOption[]) {
  return environments.filter((env) => stateEnvs[env.code]).map((env) => env.value);
}

function getAccessoryCode(item: Record<string, unknown>) {
  const raw = item.code ?? item.value ?? item.accessory_code ?? item.envcode ?? item.env_code;
  return typeof raw === "string" || typeof raw === "number" ? String(raw).trim() : "";
}

function getAccessoryReference(item: Record<string, unknown>) {
  const raw = item.reference_def ?? item.reference ?? item.label ?? item.accessory_reference;
  return typeof raw === "string" || typeof raw === "number" ? String(raw).trim() : "";
}

function mapAccessories(items: any[]) {
  return (Array.isArray(items) ? items : []).map((item) => ({
    id: String(item.id),
    code: getAccessoryCode(item),
    reference: getAccessoryReference(item),
    label: `${getAccessoryCode(item)} ${getAccessoryReference(item)}`.trim(),
  }));
}

function resolveRelations(ids: unknown, source: AccessoryOption[]) {
  if (!Array.isArray(ids)) return [];
  const byId = new Map(source.map((item) => [String(item.id), item]));
  return ids
    .map((id) => byId.get(String(id)))
    .filter((item): item is AccessoryOption => Boolean(item))
    .map((item) => ({ id: item.id, code: item.code, reference: item.reference }));
}

function filterAccessoryOptions(
  allAccessories: AccessoryOption[],
  selectedRows: RelationRow[],
  currentRowId: string | number | undefined
) {
  const selectedIds = new Set(selectedRows.map((item) => String(item.id)));
  return allAccessories
    .filter((item) => String(item.id) !== String(currentRowId ?? ""))
    .filter((item) => !selectedIds.has(String(item.id)))
    .map((item) => ({ value: item.id, label: item.label }));
}

const AccessorySuperEditPopup: React.FC<AccessorySuperEditPopupProps> = ({
  isOpen,
  onClose,
  request,
  mode = "edit",
  row,
  envNumeric,
  lang,
  onSaved,
}) => {
  const { t } = useTranslation();
  const isEditMode = mode === "edit";
  const [isSaving, setIsSaving] = useState(false);
  const [showDepSelector, setShowDepSelector] = useState(false);
  const [showIncSelector, setShowIncSelector] = useState(false);
  const [allAccessories, setAllAccessories] = useState<AccessoryOption[]>([]);
  const [depAcc, setDepAcc] = useState<RelationRow[]>([]);
  const [incAcc, setIncAcc] = useState<RelationRow[]>([]);
  const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GroupOption | null>(null);
  const [envChecks, setEnvChecks] = useState<Record<string, boolean>>({});
  const { data: rawEnvironments = [] } = useGetEnvironnementsQuery("");
  const environments = useMemo<EnvironmentOption[]>(() => {
    return (Array.isArray(rawEnvironments) ? rawEnvironments : []).map((env: any) => ({
      value: String(env.value ?? env.id ?? ""),
      code: String(env.code ?? ""),
      label: String(env.label ?? env.country ?? env.text ?? env.code ?? env.value ?? env.id ?? ""),
    }));
  }, [rawEnvironments]);
  const [createAccessory] = useCreateAccessoryMutation();
  const [updateAccessory] = useUpdateAccessoryMutation();
  const [fetchAllAccessories] = useLazyGetAllAccessoriesQuery();
  const [fetchAccessoryGroups] = useLazyGetAccessoryGroupsQuery();
  const [fetchDepInc] = useLazyGetAccessoryDepIncQuery();

  const { control, reset, getValues, setError, clearErrors } = useCustomForm<{
    reference_def: string;
    code: string;
    photo: string;
  }>({
    defaultValues: {
      reference_def: "",
      code: "",
      photo: "",
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    setIsSaving(false);
    setShowDepSelector(false);
    setShowIncSelector(false);
    reset({
      reference_def: String(row?.reference_def ?? row?.accessory_reference ?? ""),
      code: String(row?.code ?? row?.accessory_code ?? ""),
      photo: String(row?.photo ?? row?.accessory_photo ?? ""),
    });
    setEnvChecks(toCheckedEnvMap(row, environments));

    const rowCode = String(row?.code ?? row?.accessory_code ?? "").trim();

    Promise.all([
      fetchAllAccessories().unwrap().catch(() => []),
      fetchAccessoryGroups(lang).unwrap().catch(() => []),
      isEditMode && rowCode && envNumeric != null
        ? fetchDepInc({ code: rowCode, env: String(envNumeric) }).unwrap().catch(() => [])
        : Promise.resolve([]),
    ]).then(([allAccessoriesResult, groupResult, depIncResult]) => {
      const mappedAccessories = mapAccessories(allAccessoriesResult as any[]);
      const relationSource = mapAccessories(
        Array.isArray(depIncResult) && depIncResult.length > 0 ? depIncResult : (allAccessoriesResult as any[])
      );

      setAllAccessories(mappedAccessories);
      setGroupOptions(
        (Array.isArray(groupResult) ? groupResult : []).map((group: any) => ({
          value: String(group.id),
          label: String(group.description ?? group.label ?? group.value ?? ""),
          text: String(group.description ?? group.label ?? group.value ?? ""),
        }))
      );
      setSelectedGroup(
        row?.accessorygroup_id != null || row?.group
          ? {
              value: String(row?.accessorygroup_id ?? ""),
              label: String(row?.group ?? ""),
              text: String(row?.group ?? ""),
            }
          : null
      );
      setDepAcc(resolveRelations(row?.dep, relationSource));
      setIncAcc(resolveRelations(row?.inc, relationSource));
    });
  }, [
    envNumeric,
    environments,
    fetchAccessoryGroups,
    fetchAllAccessories,
    fetchDepInc,
    isEditMode,
    isOpen,
    lang,
    reset,
    row,
  ]);

  const currentRowId = row?.id ?? row?.key;
  const depOptions = useMemo(
    () => filterAccessoryOptions(allAccessories, depAcc, currentRowId),
    [allAccessories, currentRowId, depAcc]
  );
  const incOptions = useMemo(
    () => filterAccessoryOptions(allAccessories, incAcc, currentRowId),
    [allAccessories, currentRowId, incAcc]
  );

  const relationCodeById = useMemo(() => {
    return new Map(allAccessories.map((item) => [String(item.id), item.code]));
  }, [allAccessories]);

  const handleDeleteRelation = (id: string, type: "dep" | "inc") => {
    if (type === "dep") {
      setDepAcc((prev) => prev.filter((item) => String(item.id) !== String(id)));
      return;
    }
    setIncAcc((prev) => prev.filter((item) => String(item.id) !== String(id)));
  };

  const handleSave = async () => {
    if (!request) return;

    const rowId = row?.id ?? row?.key;
    if (isEditMode && rowId == null) return;

    const { reference_def, code, photo } = getValues();
    const normalizedReference = String(reference_def ?? "").trim();
    const normalizedCode = String(code ?? "").trim();
    let hasError = false;

    if (!normalizedReference) {
      setError("reference_def", { type: "required", message: t("Required") });
      hasError = true;
    }

    if (!normalizedCode) {
      setError("code", { type: "required", message: t("Required") });
      hasError = true;
    }

    if (hasError) return;
    clearErrors(["reference_def", "code"]);

    const checkedEnvs = getCheckedEnvIds(envChecks, environments);
    const countries = environments
      .filter((env) => envChecks[env.code])
      .map((env) => env.code)
      .join(", ");

    const baseUrl = request.url.replace(/\/[^/]+$/, "");
    const url = isEditMode ? `${baseUrl}/${rowId}` : baseUrl;

    const payload: Record<string, unknown> = {
      id: isEditMode ? rowId : null,
      reference_def: normalizedReference,
      accessory_reference: normalizedReference,
      code: normalizedCode,
      accessory_code: normalizedCode,
      photo,
      accessory_photo: photo,
      env: envNumeric,
      envs: checkedEnvs,
      dep: depAcc.map((item) => item.id),
      inc: incAcc.map((item) => item.id),
      accessorygroup_id: selectedGroup?.value ?? null,
      group: selectedGroup?.label ?? null,
      countries,
      lang,
    };

    setIsSaving(true);
    try {
      if (isEditMode) {
        await updateAccessory({ url, data: payload }).unwrap();
      } else {
        await createAccessory({ url, data: payload }).unwrap();
      }
      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={t("Infos")}
      onClose={onClose}
      portal
      fixSize="large"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {t(isEditMode ? "Update" : "Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group data-direction="column" data-gap="20" data-space="40" data-space-horizontal="40" data-space-top="5" data-height="autofit">
        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("reference")} name="reference_def" control={control} type="text" size="large" required />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("code")} name="code" control={control} type="text" size="large" required />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input
              dataLength="autofit"
              label={t("group")}
              name="accessorygroup_id"
              type="select"
              size="large"
              value={selectedGroup?.value ?? undefined}
              options={groupOptions}
              allowSearch
              onCustomChange={(_, value) => {
                const selectedValue =
                  typeof value === "string" || typeof value === "number" ? String(value) : "";
                const selected = groupOptions.find((item) => String(item.value) === selectedValue);
                setSelectedGroup(
                  selected
                    ? { value: String(selected.value), label: String(selected.label), text: String(selected.text) }
                    : null
                );
              }}
              placeholder={t("group")}
            />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input dataLength="autofit" label={t("photo")} name="photo" control={control} type="text" size="large" />
          </group>
        </group>

        <group data-direction="column" data-gap="8" data-space-top="10">
          <text data-weight="700">{t("environnement_mgt")}</text>
          <group data-gap="20" data-wrap="wrap">
            {environments.length === 0 && <text>{t("No data")}</text>}
            {environments.map((env, index) => (
              <group key={`${env.code}-${env.value}-${index}`} data-width="auto">
                <Input
                  id={`accessory-super-env-${env.code}-${index}`}
                  type={InputType.Checkbox}
                  name={`env_${env.value}`}
                  label={env.label}
                  checked={Boolean(envChecks[env.code])}
                  onCustomChange={(_name, checked) => {
                    setEnvChecks((prev) => ({ ...prev, [env.code]: Boolean(checked) }));
                  }}
                />
              </group>
            ))}
          </group>
        </group>

        <group data-gap="20" data-space-top="10" data-align="start">
          <group data-direction="column" data-fit="1" data-gap="8">
            <group data-justify="space-between" data-align="center" data-gap="10">
              <text data-weight="700">{t("Dependant_accessories")}</text>
              <Button
                type="button"
                primary
                aria-label={showDepSelector ? t("Back") : t("Add")}
                onClick={() => setShowDepSelector((prev) => !prev)}
              >
                <icon>{showDepSelector ? "arrow_back" : "add"}</icon>
              </Button>
            </group>

            {showDepSelector && (
              <AntSelect
                mode="multiple"
                style={{ width: "100%" }}
                value={depAcc.map((item) => item.id)}
                options={depOptions}
                onChange={(values) => {
                  const next = allAccessories
                    .filter((item) => values.map(String).includes(String(item.id)))
                    .map((item) => ({ id: item.id, code: item.code, reference: item.reference }));
                  setDepAcc(next);
                }}
                showSearch
                optionFilterProp="label"
              />
            )}

            <Table
              size="small"
              pagination={false}
              dataSource={depAcc.map((item) => ({ key: item.id, ...item }))}
              columns={[
                {
                  title: t("code"),
                  dataIndex: "code",
                  key: "code",
                  width: 180,
                  render: (value: string, record: RelationRow) => value || relationCodeById.get(String(record.id)) || "",
                },
                { title: t("reference"), dataIndex: "reference", key: "reference" },
                {
                  title: t("Action"),
                  key: "action",
                  width: 90,
                  render: (_: unknown, record: RelationRow) => (
                    <Button type="button" mini {...{ "data-color": "error-color" }} onClick={() => handleDeleteRelation(record.id, "dep")}>
                      <icon>delete</icon>
                    </Button>
                  ),
                },
              ]}
              locale={{ emptyText: t("No dependant accessories") }}
            />
          </group>

          <group data-direction="column" data-fit="1" data-gap="8">
            <group data-justify="space-between" data-align="center" data-gap="10">
              <text data-weight="700">{t("Invalid_accessories")}</text>
              <Button
                type="button"
                primary
                aria-label={showIncSelector ? t("Back") : t("Add")}
                onClick={() => setShowIncSelector((prev) => !prev)}
              >
                <icon>{showIncSelector ? "arrow_back" : "add"}</icon>
              </Button>
            </group>

            {showIncSelector && (
              <AntSelect
                mode="multiple"
                style={{ width: "100%" }}
                value={incAcc.map((item) => item.id)}
                options={incOptions}
                onChange={(values) => {
                  const next = allAccessories
                    .filter((item) => values.map(String).includes(String(item.id)))
                    .map((item) => ({ id: item.id, code: item.code, reference: item.reference }));
                  setIncAcc(next);
                }}
                showSearch
                optionFilterProp="label"
              />
            )}

            <Table
              size="small"
              pagination={false}
              dataSource={incAcc.map((item) => ({ key: item.id, ...item }))}
              columns={[
                {
                  title: t("code"),
                  dataIndex: "code",
                  key: "code",
                  width: 180,
                  render: (value: string, record: RelationRow) => value || relationCodeById.get(String(record.id)) || "",
                },
                { title: t("reference"), dataIndex: "reference", key: "reference" },
                {
                  title: t("Action"),
                  key: "action",
                  width: 90,
                  render: (_: unknown, record: RelationRow) => (
                    <Button type="button" mini {...{ "data-color": "error-color" }} onClick={() => handleDeleteRelation(record.id, "inc")}>
                      <icon>delete</icon>
                    </Button>
                  ),
                },
              ]}
              locale={{ emptyText: t("No invalid accessories") }}
            />
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default AccessorySuperEditPopup;